import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { RosterService } from '../services/roster.service';
import { min, max, eachDayOfInterval, eachMonthOfInterval, eachYearOfInterval, format, addDays, lastDayOfMonth, isSameDay, differenceInDays, addMonths, parseISO, subDays } from 'date-fns';
import { EmployeeRosterFormComponent } from './employee-roster-form.component';
import { forkJoin } from 'rxjs';
import { PpsUtilityService } from '../services/pps-utility.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { IEmployeeRoster } from '../shared/models/roster/employee-roster';
import { IRosterValue } from "../shared/models/roster/roster-value";
import { IRosterCode } from '../shared/models/roster-code';
import { ToastService } from '../shared/toast/toast.service';
import * as _ from 'lodash-es';
import { AuthenticationService } from '../services/authentication.service';
import { BusinessOperation } from '../shared/models/business-operation';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { SubSink } from 'subsink';
import { RosterTooltipComponent } from './roster-tooltip.component';
import { IEmployeeRosterUpdateByRuleModel } from '../shared/models/roster/employee-roster-update-by-rule-model';
import { IRosterSummaryLineData } from '../shared/models/roster/roster-summary-line-data';
import { ActivatedRoute } from '@angular/router';
import { DeleteActionComponent } from '../system-settings/delete-action.component';
import { EmployeeService } from '../services/employee.service';
import { RosterCodeSettingService } from '../services/roster-code-setting.service';
import { DateRangeFormComponent } from '../shared/date-range-form.component';
import { IDateRange } from '../shared/models/date-range';
import { CellClickedEvent, CellDoubleClickedEvent, CellKeyDownEvent, 
         FilterChangedEvent, GridReadyEvent, HeaderClassParams, 
         RowClickedEvent, RowDragMoveEvent, RowNode, SortChangedEvent } from 'ag-grid-community';
import { IEmployeeSortNoUpdateModel } from '../shared/models/roster/employee-sortno-update-model';
import { UpdateTravelStatusFormComponent } from './update-travel-status-form.component';
import { IUpdateTravelStatusModel } from '../shared/models/roster/update-travel-status-model';
import { EmployeeFilterComponent } from './employee-filter.component';
import { IEmployeeHighlight, IEmployeeHighlightUpdate, IRosterEmployeeHighlight } from '../shared/models/roster/employee-highlight';
import { ISelectOption } from '../shared/models/option';
import { EmployeeFloatingFilterComponent } from './employee-floating-filter.component';
import { Employee } from '../shared/models/employee';
import { EmployeeFormComponent } from '../employees/employee-form.component';
import { IEmployeeUpdateEntities } from '../shared/models/employee-update-entities';
import { IRosterEmployeeViewModel } from '../shared/models/roster/roster-employee-view-model';
import { GridActionComponent } from '../shared/grid-action.component';
import { EmployeeNameRenderer } from './employee-name-renderer.component';
import { IApiResult } from '../shared/models/api-result';
import { IRosterResult } from '../shared/models/roster/roster-result';
import { IRosterProject } from '../shared/models/roster/roster-project';
import { parseIsolatedEntityName } from 'typescript';
import { PayrollRosterCodeRecommendor } from './payroll-roster-code-recommendor';
import { RosterCodeService } from '../services/roster-code.service';
import { IEmployeeSimpleModel } from '../shared/models/roster/employee-simple-model';
import { DeleteRosterDataFormComponent } from './delete-roster-data-form.component';
import { IRosterDataClearModel } from '../shared/models/roster/roster-data-clear-model';
import { IRosterEmployeeNote } from '../shared/models/roster/roster-employee-note';
import { RosterEmployeeNoteFormComponent } from './roster-employee-note-form.component';
import { IEmployeeRosterUpdateModel } from '../shared/models/roster/employee-roster-update-model';
import { environment } from 'src/environments/environment';

interface IEmployeeOrder{
  [id:number]: number;
}

interface IColumn{
  colId: string,
  columnName: string
}

@Component({
  selector: 'pps-roster',
  templateUrl: './roster.component.html',
  styleUrls: ['./roster.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class RosterComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  smallScreen: boolean = false;

  rosterProject: IRosterProject = null;
  isActiveRoster: boolean = true;

  items: MenuItem[] = [];
  spinnerName: string = "rosterSpinner";
  defaultSpinnerText: string = "Loading roster data ...";
  spinnerText: string = "Loading roster data ...";

  // For show/hide employee columns multi-select component
  employeeColumns: IColumn[] = [
    {colId: "company", columnName: "Company"},
    {colId: "position", columnName: "Position"},
    {colId: "supervisor", columnName: "Supervisor"},
    {colId: "workarea", columnName: "Work Area"},
    {colId: "phone", columnName: "Phone"},
    {colId: "email", columnName: "Email"},
    {colId: "dateOfBirth", columnName: "Date of Birth"},
    {colId: "accessId", columnName: "Access Id"}
  ];
  defaultVisibleColumns: IColumn[] = [
    {colId: "position", columnName: "Position"},
    {colId: "supervisor", columnName: "Supervisor"},
    {colId: "phone", columnName: "Phone"},
    {colId: "accessId", columnName: "Access Id"}
  ];
  selectedEmployeeColumns: IColumn[] = [];
  private columnDisplaySettingsKey = "column-display-settings"; //this key should be the same as the value in index.html
  
  employeeUpdateEntities: IEmployeeUpdateEntities = null;

  // yearRange is used by p-calendar control yearRange attribute. It's always between 2018 and 3 years after this year.
  yearRange: string = `2018:${(new Date().getFullYear()) + 5}`;

  employeeRosters: IEmployeeRoster[] = [];
  employeeOrders: IEmployeeOrder = {};
  rosterEmployeeNotes: {
    [empId_colId: string]: IRosterEmployeeNote
  } = {};

  tooltipShowDelay: number = 0;
  frameworkComponents = {customTooltip: RosterTooltipComponent};

  selectedEmployee: IEmployeeRoster = null;

  gridHeaders;
  gridOptions: any = {
    context: {componentParent: this},
    onRowClicked: this.onRowClicked,
    onCellClicked: this.onCellClicked,
    getRowId: param => {
      const row = param.data as any;
      const rowNo = row?.rowNo ?? (param as any).rowIndex ?? 0;

      if (row?.employee?.id > 0) {
        return `${row.employee.id}`;
      }

      if (row?.isGroupLine) {
        return `group_${row?.employee?.workGroupId ?? rowNo}`;
      }

      if (row?.isSummaryLine) {
        return `summary_${row?.title ?? rowNo}`;
      }

      return `row_${rowNo}`;
    },
    defaultColDef: {
      filter: false, 
      sortable: false,
      pinned: true,
      resizable: true,
      suppressMovable: true,
      enableCellChangeFlash: true,
      floatingFilter: false      
    }
  };

  startDate: Date;
  endDate: Date;
  generateRosterDataEndDate: Date = addDays(new Date(), 60);

  private summaryLineDisplaySettingsKey = "summary-line-display-settings"; //this key should be the same as the value in index.html
  summaryLineData : IRosterSummaryLineData[] = [];
  pinnedSummaryLineData : IRosterSummaryLineData[] = [];
  storedSummaryLines: string[] = [];

  generatingSignOnSheet = false;
  updatingTravelStatus = false;
  exportingRoster = false;

  private gridApi;
  private gridColumnApi;
  private rosterCodes: IRosterCode[] = [];
  private clipboardData: IRosterValue = null;

  private sortActive = false;
  private filterActive = false;
  
  // Sample data:
  // {
  //   1_0: {foregroundColor:"#ffffff", backgroundColor:"#000000"},
  //   2_0: {foregroundColor:"#ffffff", backgroundColor:"#000000"}
  // }
  private rosterCodeSettingsObject: {
    [rosterCodeId_ticketStatusId: string]: {
      foregroundColor:string,
      backgroundColor:string
    }
  } = {};

  // Sample data:
  // {
  //    "20200101": "New years day",
  //    "20201224": "Christmas Eve"
  // }
  //
  private holidayObject: {[day: string] : string} = {};

  public dateRanges: {id:string, title: string}[] = [];
  public selectedDateRange: {id:string, title: string} = null;

  showEmptyPayrollRosterCode = false;

  constructor(private rosterService: RosterService,
              private _rosterCodeSettingService: RosterCodeSettingService,
              private _payrollRosterCodeRecommendor: PayrollRosterCodeRecommendor,
              private _rosterCodeService: RosterCodeService,
              private _empService: EmployeeService,
              private util: PpsUtilityService,
              private spinner: NgxSpinnerService,
              private toast: ToastService,
              private _activatedRoute: ActivatedRoute,
              private _dialog: DialogService,
              private _confirm: ConfirmationService,
              private authService: AuthenticationService,
              private breakpointObserver: BreakpointObserver) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
        .subscribe((state: BreakpointState) => {
          this.smallScreen = !state.matches;
        });
  }

  /**
   * Cleanup actions before the component is destroyed:
   * 1. Make sure the re-ordered employee display order is persistant to the database
   * 2. Make sure all subscriptions are unsubscribed
   */
  ngOnDestroy(): void {
    this.updateEmployeeSortNo(() => this.subs.unsubscribe());
  }

  /**
   * This is to make sure same actions in ngOnDestroy are performed when user perform browser refresh.
   * When user click the refresh button in browser, the ngOnDestroy() will NOT be triggered. This method will be
   * triggered instead.
   * @param event 
   */
  @HostListener('window:beforeunload', [ '$event' ])
  beforeUnloadHandler(event) {
    this.updateEmployeeSortNo(() => this.subs.unsubscribe());
  }  
  
  /**
   * Initialize the roster component. 
   * Querystring will be checked to determine which data should be loaded. When active-roster is true, it will display 
   * active roster, otherwise it will display inactive roster.
   */
  ngOnInit(): void {
    this._activatedRoute.queryParams.subscribe(params => {
      this.isActiveRoster = params['active-roster'] === "true" ? true : false;
      var rosterProjectId = +params['roster-project'];
      this.rosterProject = this.authService.userContext.rosterProjects.find(rp => rp.id === rosterProjectId);

      let item: MenuItem = this.isActiveRoster ?
        {label: "Active Roster", icon: "pi-fw fas fa-calendar-check"} :
        {label: "Inactive Roster", icon: "pi-fw far fa-calendar-times"};
      
      this.items = [{label: "Roster", icon: "pi-fw fas fa-calendar-alt"}, item];
      
      var selectedItems = window.localStorage.getItem(this.columnDisplaySettingsKey);
      if (selectedItems){
        this.selectedEmployeeColumns = JSON.parse(selectedItems);
      }else{
        this.selectedEmployeeColumns = this.defaultVisibleColumns;
      }
      
      if (!this.canModifyRoster()){
        this.selectedEmployeeColumns = this.defaultVisibleColumns;
      }

      this.storedSummaryLines = [];
      var summaryLines = window.localStorage.getItem(this.getSummaryLineDisplaySettingsKey());
      if (summaryLines){
        this.storedSummaryLines = JSON.parse(summaryLines);
      }

      this.initialize();
    });
  }

  showHideEmployeeColumns(){
    var state = [];
    this.employeeColumns.forEach(c => {
      if (this.selectedEmployeeColumns.findIndex(sc => sc.colId === c.colId) === -1){
        state.push({colId: c.colId, hide: true});
      }else{
        state.push({colId: c.colId, hide: false})
      }
    });

    this.gridColumnApi.applyColumnState({
      state: state
    });

    window.localStorage.setItem(this.columnDisplaySettingsKey, JSON.stringify(this.selectedEmployeeColumns));
  }

  showHideSummaryLine(){
    var summaryLines = this.pinnedSummaryLineData.map(s=>s.title);
    window.localStorage.setItem(this.getSummaryLineDisplaySettingsKey(), JSON.stringify(summaryLines));
  }

  /**
   * Setup global gridApi and gridColumnApi and also suppress the row drag handle if user has no permission to rearrange
   * roster employee display order. This is callback function to "gridReady" event defined in <ag-grid-angular>.
   * @param gridReadyEvent GridReadyEvent defined in ag-grid.
   * https://www.ag-grid.com/angular-data-grid/grid-events/#reference-miscellaneous 
   */
  onGridReady(gridReadyEvent: GridReadyEvent) {
    this.gridApi = gridReadyEvent.api;
    // AG Grid v32 exposes column APIs on gridApi and removes columnApi getter.
    this.gridColumnApi = gridReadyEvent.api;
    this.setSuppressRowDrag(!this.canModifyEmployeeRosterSortNo());
    this.showHideEmployeeColumns();
  }

  private setGridRowData(rowData: any[]) {
    if (!this.gridApi) {
      return;
    }

    this.gridApi.setGridOption("rowData", rowData);
  }

  private setGridColumnDefs(columnDefs: any[]) {
    if (!this.gridApi) {
      return;
    }

    if (typeof this.gridApi.setGridOption === "function") {
      this.gridApi.setGridOption("columnDefs", columnDefs);
      return;
    }
  }

  private setPinnedBottomRowData(rows: any[]) {
    if (!this.gridApi) {
      return;
    }

    if (typeof this.gridApi.setGridOption === "function") {
      this.gridApi.setGridOption("pinnedBottomRowData", rows);
      return;
    }

    if (typeof this.gridApi.setPinnedBottomRowData === "function") {
      this.gridApi.setPinnedBottomRowData(rows);
    }
  }

  private setSuppressRowDrag(suppressRowDrag: boolean) {
    if (!this.gridApi) {
      return;
    }

    if (typeof this.gridApi.setGridOption === "function") {
      this.gridApi.setGridOption("suppressRowDrag", suppressRowDrag);
      return;
    }

    if (typeof this.gridApi.setSuppressRowDrag === "function") {
      this.gridApi.setSuppressRowDrag(suppressRowDrag);
    }
  }

  private getGridRowNode(rowId: string | number) {
    if (!this.gridApi || rowId === null || rowId === undefined) {
      return null;
    }

    return this.gridApi.getRowNode(`${rowId}`);
  }

  /**
   * Capture currently selected employee. If user clicked on group line ot summary line, currently selected employee 
   * will be reset to null.
   * @param evt RowClickedEvent defined in ag-grid
   * https://www.ag-grid.com/angular-data-grid/grid-events/#reference-miscellaneous 
   */
  onRowClicked(evt: RowClickedEvent){
    let _this = evt.context.componentParent;
    _this.selectedEmployee = evt.data;
    if (_this.selectedEmployee.isGroupLine || _this.selectedEmployee.isSummaryLine){
      _this.selectedEmployee = null;
    }
  }  

  /**
   * Highlight/unhighlight a cell when ctrl+click is detected
   * @param clickEvent CellClickedEvent defined in ag-grid
   * https://www.ag-grid.com/angular-data-grid/grid-events/#reference-miscellaneous 
   */
  onCellClicked(clickEvent: CellClickedEvent){
    // Ctrl + Click to highlight a cell
    if (clickEvent.event["ctrlKey"]){
      let _this = clickEvent.context.componentParent;
      var employeeRoster = clickEvent.data as IEmployeeRoster;
      var day = clickEvent.colDef["rosterDay"];

      if (day){
        // highlight roster value cell
        var oldRosterValue: IRosterValue = employeeRoster.values[format(day,"yyyyMMdd")];
        var rosterValue: IRosterValue = _.cloneDeep(oldRosterValue);
        rosterValue.hl = !rosterValue.hl;

        let result = {
          employeeId: employeeRoster.employee.id,
          section: 2,
          oldRosterValue: oldRosterValue,
          rosterValue: rosterValue,
          from: day,
          to: day
        };      
        
        _this.updateEmployeeRoster(result);
      }else{
        // highlight roster employee section cell (employeeName, phone, email and dateOfBirth)
        if (_this.canColumnBeHighlighted(clickEvent.colDef.colId)){
          _this.highlightEmployee(clickEvent.node, employeeRoster, {employeeId: employeeRoster.employee.id, colId: clickEvent.colDef.colId});
        }        
      }
    }
  }

  /**
   * Callback actions when double click event detected on a roster cell:
   * 1. Do nothing if user has no permission to modify roster
   * 2. Do nothing if user clicked on work group line or summary line
   * 3. Open employee edit form if clicked on employee name and user has permission to modify employee data
   * 4. Show config roster code form if clicked on a roster code
   * @param event CellDoubleClickedEvent defined in ag-grid
   */
  onCellDoubleClicked(event: CellDoubleClickedEvent){
    let _this = event.context.componentParent;

    if (!_this.canModifyRoster()){
      return;
    }

    let employeeRoster = event.data as IEmployeeRoster;
    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      return;
    }

    let selectedDate = event.colDef["rosterDay"];
    if (selectedDate){
      _this.showEmployeeRosterForm(selectedDate, employeeRoster);
    }else{
      let empId = employeeRoster.employee.id;
      let colId = event.colDef.colId;
      if (colId === "employeeName"){
        this.editEmployee(event.data.employee)
      }

      if (colId === "position"){
        let empNote: IRosterEmployeeNote = this.getRosterEmployeeNote(empId, colId);
        if (!empNote){
          empNote = {
            rosterProjectId: this.rosterProject.id,
            employeeId: empId,
            columnName: colId,
            note: ""
          };
        }
        

        if (_this.shouldShowRosterEmployeeNoteForm(employeeRoster)){
          _this.showRosterEmployeeNoteForm(empNote, employeeRoster);
        }
      }
    }
  }

  /**
   * Should popup roster employee note form if 
   * it's active employee in active roster or
   * it's inactive employee in inactive roster
   * @param employeeRoster IEmployeeRoster
   * @returns true if it's active employee in active roster or 
   * inactive employee in inactive roster. false otherwise
   */
  private shouldShowRosterEmployeeNoteForm(employeeRoster: IEmployeeRoster): boolean{
    return (this.isActiveRoster && employeeRoster.employee.active) || 
      (!this.isActiveRoster && !employeeRoster.employee.active);
  }

  private _employeeRosterForm = null;
  private showEmployeeRosterForm(selectedDate: Date, employeeRoster: IEmployeeRoster){
    let selectedDateStr = format(selectedDate, "yyyyMMdd");
    let prevDateStr = format(subDays(selectedDate, 1), "yyyyMMdd");
    let nextDateStr = format(addDays(selectedDate, 1), "yyyyMMdd");
    let empName = this.util.getEmployeeName(employeeRoster.employee);

    this.closeEmployeeRosterForm();

    let data = {
      date: selectedDate,
      minDate: new Date(this.startDate.getTime()),
      maxDate: new Date(this.endDate.getTime()),
      employeeName: empName,
      employeeId: employeeRoster.employee.id,
      rosterCodes: this.rosterCodes,
      selectedRosterValue: employeeRoster.values[selectedDateStr],
      previousRosterValue: employeeRoster.values[prevDateStr],
      nextRosterValue: employeeRoster.values[nextDateStr]
    };

    if (!data.nextRosterValue || !data.previousRosterValue){
      this._confirm.confirm({
        header: "Load more roster data",
        message: `Please load more roster data to continue. The system needs to know previous day and next day's roster data for validation`,
        icon: "pi pi-ban text-danger",
        key: "invalidPayrollRosterCodePromptDialog1",
        acceptVisible: true,
        rejectVisible: false,
        acceptLabel: "OK"
      });

      return;
    }    

    this._employeeRosterForm = this._dialog.open(EmployeeRosterFormComponent, {
      header: `Config Roster Code for ${empName}`,
      styleClass: "size-md",
      data: data,
      draggable: true
    });

    let UpdateRosterValue = 1;
    let UpdateRosterNote = 2;
    let UpdateBoth = 3;
    this.subs.sink = this._employeeRosterForm.onClose.subscribe(result => {
      if (result){
        if (result.section === UpdateRosterValue){
          this.updateEmployeeRoster(result);
        }

        if (result.section === UpdateRosterNote){
          this.saveRosterNote(selectedDate, result);
        }

        if (result.section === UpdateBoth){
          this.updateEmployeeRoster(result, () => {
            var fromDate = parseISO(result.from);
            var toDate = parseISO(result.to);

            let selectedDays = eachDayOfInterval({start: fromDate, end: toDate});
            let counter = 0;
            selectedDays.forEach(day => {
              let resultTemp = _.cloneDeep(result);
              resultTemp.rosterNote.day = day;
              if (resultTemp.rosterValue.sn){
                resultTemp.rosterValue.sn += counter;
              }
              
              this.saveRosterNote(day, resultTemp);
              counter ++;              
            });
          });
        }
      }},
      reason => {
      }
    );
  }

  private closeEmployeeRosterForm(){
    if (this._employeeRosterForm){
      this._employeeRosterForm.close();
      this._employeeRosterForm = null;
    }
  }

  private showRosterEmployeeNoteForm(empNote: IRosterEmployeeNote, employeeRoster: IEmployeeRoster){
    let empName = this.util.getEmployeeName(employeeRoster.employee);

    let dialog = this._dialog.open(RosterEmployeeNoteFormComponent, {
      header: `Note for ${empName}`,
      styleClass: "size-md",
      data: empNote,
      draggable: true
    });

    this.subs.sink = dialog.onClose.subscribe(
      (empNote: IRosterEmployeeNote) => {
        if (empNote){
          this.subs.sink = this.rosterService.updateRosterEmployeeNote(empNote)
            .subscribe(res =>{
              if (res.succeed){
                let updatedEmpNote:IRosterEmployeeNote = res.result;
                this.rosterEmployeeNotes[`${updatedEmpNote.employeeId}-${updatedEmpNote.columnName}`] = updatedEmpNote;

                // refresh grid row
                let rowNode = this.getGridRowNode(employeeRoster.employee.id);
                if (rowNode){
                  rowNode.setData(employeeRoster);
                  this.gridApi.redrawRows({rowNodes:[rowNode]});
                }
              }
            });
        }
      },
      reason => {
      }
    );
  }


  /**
   * Handle copy, paste and delete when ctrl+c, ctrl+v or Del key press event detected
   * @param cellKeyPressEvent CellKeyDownEvent defined in ag-grid 
   * (https://www.ag-grid.com/angular-data-grid/grid-events/) 
   */
  onCellKeyPress(event: any){
    switch(event.event["code"]){
      case "KeyC":   // Ctrl C
        if (event.event["ctrlKey"] && this.canModifyRoster()){
          this.clipboardData = this.getRosterValueFromSelectedCell(event);
        }
        break;
      case "KeyV":   // Ctrl V
        if (event.event["ctrlKey"] && this.canModifyRoster()){ 
          this.pasteRosterData(event);
        }
        break;
      case "Delete": // Delete
        if (this.canModifyRoster()){
          this.deleteRosterData(event); 
        }
        break;
    }
  }

  /**
   * suppress row drag while sorting is active. 
   * The roster module use unmanaged dragging which by default allowed while sort is applied
   * https://www.ag-grid.com/angular-data-grid/row-dragging/#suppress-row-drag
   * @param event SortChangedEvent defined in ag-grid 
   * (https://www.ag-grid.com/angular-data-grid/grid-events/#reference-sort)
   */
  onSortChanged(event: SortChangedEvent) {
    var colState = this.gridColumnApi.getColumnState() || [];
    this.sortActive = colState.some((c) => c.sort);

    // suppress row drag if either sort or filter is active
    var suppressRowDrag = this.sortActive || this.filterActive;

    this.setSuppressRowDrag(suppressRowDrag);
  }

  /**
   * suppress row drag while filter is active. 
   * The roster module use unmanaged dragging which by default allowed while filter is applied
   * https://www.ag-grid.com/angular-data-grid/row-dragging/#suppress-row-drag
   * @param event FilterChangedEvent defined in ag-grid 
   * (https://www.ag-grid.com/angular-data-grid/grid-events/#reference-sort)
   */  
  onFilterChanged(event: FilterChangedEvent) {
    this.filterActive = this.gridApi.isAnyFilterPresent();
    // suppress row drag if either sort or filter is active
    var suppressRowDrag = this.sortActive || this.filterActive;
    this.setSuppressRowDrag(suppressRowDrag);
  }  

  /**
   * Dragging the row to different place. If row data can not be moved if the moving row and targnet row are not in the
   * same employee workgroup or target row is not an employee line.
   * @param event RowDragMoveEvent defined in ag-grid
   */
  onRowDragMove(event: RowDragMoveEvent) {
    var movingNode = event.node;
    var overNode = event.overNode;
    
    var rowNeedsToMove = movingNode !== overNode;
    if (rowNeedsToMove) {
      // the list of rows we have is data, not row nodes, so extract the data
      var movingData = movingNode.data as IEmployeeRoster;
      var overData = overNode!.data as IEmployeeRoster;
      if (overData.employee.id === 0 || movingData.employee.workGroupId !== overData.employee.workGroupId){
        return;
      }

      var fromIndex = this.employeeRosters.indexOf(movingData);
      var toIndex = this.employeeRosters.indexOf(overData);
      
      var newStore = this.employeeRosters.slice();

      moveInArray(newStore, fromIndex, toIndex);

      this.employeeRosters = newStore;
      
      this.setGridRowData(this.employeeRosters);
      this.gridApi.clearFocusedCell();
    }
    
    function moveInArray(arr: any[], fromIndex: number, toIndex: number) {
      var element = arr[fromIndex];
      arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, element);
    }
  }

  focusOnInvalidPayrollRosterCodeCell(cellLocation){
    let rowId = cellLocation.rowId;
    let colId = cellLocation.colId;
    let autoOpenForm = cellLocation.autoOpenForm;
    let rowNode = this.getGridRowNode(rowId);
    let column = this.gridColumnApi.getColumn(colId);

    if (rowNode){
      this.gridApi.ensureNodeVisible(rowNode, "top");
      this.gridApi.ensureColumnVisible(colId, "start");
      this.gridApi.setFocusedCell(rowNode.rowIndex, colId);
      if (column && autoOpenForm){
        let selectedDate = column.colDef.rosterDay;
        let employeeRoster = rowNode.data as IEmployeeRoster;
        this.showEmployeeRosterForm(selectedDate, employeeRoster);
      }else{
        this.closeEmployeeRosterForm();
      }
    }
  }

  /**
   * Update travel status for selected employee within selected date range. For FIA, FOA, FIP, FOP, DIA, DOA, DIP and 
   * DOP, they all have a travel status (Approved, Planning, Pending). The method allow to batch update the travel 
   * status in selected range instead of update the travel status individually.
   * This method will be invoked when "Update Travel Status" button is clicked. This button will be disabled if no
   * employee is selected.
   */
  updateTravelStatus(){
    let dialog = this._dialog.open(UpdateTravelStatusFormComponent, {
      header: `Update Travel Status for ${this.util.getEmployeeName(this.selectedEmployee.employee)}`,
      styleClass: "size-md",
      data:{
        initialValues:{
          presetDateRange: null,
          fromDate: this.startDate,
          toDate: this.endDate
        }
      },
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      (updateTravelStatusModel: IUpdateTravelStatusModel) => {
        if (updateTravelStatusModel){
          updateTravelStatusModel.employeeId = this.selectedEmployee.employee.id;

          this.updatingTravelStatus = true;
          this.spinnerText = `Updating ${this.util.getEmployeeName(this.selectedEmployee.employee)}'s travel status...`;
          this.spinner.show(this.spinnerName);

          this.subs.sink = this.rosterService.updateTravelStatus(this.rosterProject.id, updateTravelStatusModel)
            .subscribe(res => {
              if (res.succeed){
                var empRoster = res.result;
                this.searchEmployeeRoster(empRoster.employee?.id, (foundIndex)=>{
                  let columns = [];

                  for (var day in empRoster.values){
                    this.employeeRosters[foundIndex].values[day] = empRoster.values[day];
                    columns.push(`roster.${day}.rosterCode.code`);
                  }

                  let rowNode = this.getGridRowNode(empRoster.employee?.id);
                  if (rowNode){
                    rowNode.setData(this.employeeRosters[foundIndex]);
                    this.gridApi.flashCells({rowNodes: [rowNode], columns: columns, flashDuration: 1000, fadeDuration: 500});
                  }

                  this.toast.succeeded(`${columns.length} travel status have been updated.`);
                });
              }
              else{
                this.toast.failed("Update travel status failed: " + res.errors.join(";"));
              }
            },
            error => {
              this.updatingTravelStatus = false;
              this.spinner.hide(this.spinnerName); 
              this.spinnerText = this.defaultSpinnerText;
            },
            () => {
              this.updatingTravelStatus = false;
              this.spinner.hide(this.spinnerName); 
              this.spinnerText = this.defaultSpinnerText;
            });
        }
      },
      reason => {
      }
    );
  }

  /**
   * Generate roster data based on selected target date and roster type configured for employees. 
   * This method will be invoked when "Generate Roster Data" button is clicked.
   */
  generateRosterData(){
    let dialog = this._dialog.open(DateRangeFormComponent, {
      header: `Choose target date to generate roster data`,
      styleClass: "size-sm",
      focusOnShow: false,
      data: {
        options:{
          showPresetDateRange: false,
          showFromDate: false,
          toDateLabel: "Target date",
          maxToDate: addDays(new Date(), 365),
          minToDate: new Date(),
          prompt: `The maximum target date is ${this.util.getDateString(addDays(new Date(), 365))}`,
          promptStyleClasses: "text-bold"
        },
        initialValues: {
          presetDateRange: null,
          toDate: this.generateRosterDataEndDate
        }
      },
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      (dateRange: IDateRange) => {
        if (dateRange){
          this.generateRosterDataEndDate = dateRange.range.to;
          this.generateRosterDataHelper();
        }
      },
      reason => {
      }
    );
  }

  deleteFutureRosterData(){
    let dialog = this._dialog.open(DeleteRosterDataFormComponent, {
      header: `Delete Future Roster Data`,
      styleClass: "size-sm",
      focusOnShow: false,
      data: {
        employees: this.getRosterEmployees()
      },
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      (result: any) => {
        if (result){
          this.deleteFutureRosterDataHelper(result.deletionModel, result.selectedEmployees);
        }
      },
      reason => {
      }
    );
  }

  private deleteFutureRosterDataHelper(deletionModel: IRosterDataClearModel, emps: string){
    console.log(deletionModel);
    this._confirm.confirm({
      header: `Delete future roster data`,
      message: `WARNING: You must confirm again that you want to delete all roster data after
                ${this.util.getDateString(deletionModel.startDate)} for these employees: ${emps}.`,
      icon: "fas fa-exclamation-circle icon-danger",
      key: "danger-dialog",
      defaultFocus: "accept",
      accept: () => {
        this.subs.sink = this.rosterService.deleteFutureRosterData(this.rosterProject.id, deletionModel)
          .subscribe(res => {
            if (res.succeed){
              this.toast.succeeded(`${res.result} roster data have been deleted`);
              this.refresh();              
            }else{
              this.toast.failed(res.errors.join(";"));
            }
          });
      }
    });
  }

  /**
   * Export roster data to Excel file based on selected date range. 
   * This method will be invoked when "Export Roster" button is clicked.
   */
  exportRoster(){
    let dialog = this._dialog.open(DateRangeFormComponent, {
      header: `Export roster to Excel file`,
      styleClass: "size-sm",
      data:{
        initialValues:{
          presetDateRange: null,
          fromDate: this.startDate,
          toDate: this.endDate
        }
      },
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      (dateRange: IDateRange) => {
        if (dateRange){
          this.exportingRoster = true;
          this.spinnerText = "Preparing Excel file...";
          this.spinner.show(this.spinnerName);
          this.subs.sink = this.rosterService.exportRoster(
            this.rosterProject.id, 
            dateRange, 
            this.isActiveRoster,
            () => {
              this.exportingRoster = false;
              this.spinnerText = this.defaultSpinnerText;
              this.spinner.hide(this.spinnerName);
            },
            (errorRes) => {
              this.exportingRoster = false;
              this.spinnerText = this.defaultSpinnerText;
              this.spinner.hide(this.spinnerName);
              this.toast.failed(`${errorRes.errors.join("; ")}`);
            }
          );
        }
      },
      reason => {
      }
    );
  }  

  /**
   * Generate daily signon sheet for selected date range.
   * This method will be invoked when "Generate Signon Sheet" button is clicked.
   */
  generateDailySignonSheet(){
    let dialog = this._dialog.open(DateRangeFormComponent, {
      header: `Generate Signon Sheet`,
      styleClass: "size-sm",
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      (dateRange: IDateRange) => {
        if (dateRange){
          this.generatingSignOnSheet = true;

          this.spinnerText = `Generating signon sheet...`;
          this.spinner.show(this.spinnerName);          
          this.subs.sink = this.rosterService.generateDailySignonSheet(
            this.rosterProject.id,
            dateRange, 
            () => {
              this.generatingSignOnSheet = false;
              this.spinner.hide(this.spinnerName);
              this.spinnerText = this.defaultSpinnerText;
            },
            (errorRes) => {
              this.generatingSignOnSheet = false;
              this.spinner.hide(this.spinnerName);
              this.spinnerText = this.defaultSpinnerText;
              this.toast.failed(`${errorRes.errors.join("; ")}`);
            }
          );
        }
      });
  }
  /**
   * 
   * @param empRoster 
   * @returns 
   */
  fixPayrollRosterCode(){
    this.showEmptyPayrollRosterCode = true;
  }

  /**
   * Callback function to delete an employee line from roster. 
   * This method is invoked from DeleteActionComponent. 
   * @param empRoster IEmployeeRoster of the selected row
   * @returns 
   */
  delete(empRoster: IEmployeeRoster){
    if (empRoster === null){
      return;
    }

    this._confirm.confirm({
      header: `Remove employee from roster`,
      message: `Please confirm "${this.util.getEmployeeName(empRoster.employee)}" will be removed from roster.`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteHelper(empRoster)
    });
  }

  /**
   * Check if current user has permission to modify roster data.
   * @returns true with permission, false without.
   */
  canModifyRoster(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.ModifyRoster);
  }

  /**
   * Check if current user has permission to change employee's display order
   * @returns true with permission, false without.
   */
  canModifyEmployeeRosterSortNo(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.UpdateEmployeeRosterSortNo);
  }

  /**
   * Check if current user has permission to edit employee's information
   * @returns true with permission, false without.
   */
   canModifyEmployee(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.ModifyEmployee);
  }

  /**
   * Check if current user has permission to print signon sheet
   * @returns true with permission, false without.
   */
   canGenerateSignOnSheet(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.PrintDailySignonSheet);
  }

  /**
   * Check if current user has permission to export roster to excel file
   * @returns true with permission, false without.
   */
   canExportRoster(): boolean{
     return this.authService.canUserDoOperartion(BusinessOperation.ExportRoster);
  }
 

  /**
   * Callback function to load roster data based on selected date range.
   * This method will be invoked when "Load Roster" button is clicked.
   */
  loadRosterData(){
    let dialog = this._dialog.open(DateRangeFormComponent, {
      header: `Choose Date Range`,
      styleClass: "size-sm",
      data: {
        initialValues: {
          presetDateRange: null,
          fromDate: this.startDate,
          toDate: this.endDate
        }
      },
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      (dateRange: IDateRange) => {
        if (dateRange){
          this.startDate = dateRange.range.from;
          this.endDate = dateRange.range.to;
          this.refresh();
        }
      },
      reason => {
      }
    );
  }

  /**
   * Reload roster data for currently selected date range. After this call, the selected employee will be reset to null.
   * This method will also be invoked when "Refresh" icon is clicked.
   */
  refresh(){
    if (this.gridApi){
      this.setGridRowData([]);
      this.setGridColumnDefs([]);
    }
    
    this.selectedEmployee = null;
    this._loadRosterData();
  }

  /**
   * Save roster note to database.
   * This method is invoked when user clicked Save button in Configure Roster Code dialog.
   * @param selectedDate Date
   * @param result update intruction to backend api. The fields in section 3 will be used to update the note.
   */
  private saveRosterNote(selectedDate: Date, result){
    this.spinnerText = "Saving roster note...";
    this.spinner.show(this.spinnerName);

    this.subs.sink = this.rosterService
      .updateRosterNote(result.rosterNote)
      .subscribe(res => {
        if (res.succeed){
          result.rosterValue.rn = _.cloneDeep(res.result);
          this.updateEmployeeRosterValuesForSelectedDays(result.employeeId, [selectedDate], _.cloneDeep(result.rosterValue));
        }else{
          console.log("Update roster note failed: " + res.errors.join(";"));
        }
      },
      error => {
        this.spinner.hide(this.spinnerName);
        this.spinnerText = this.defaultSpinnerText;
      },
      () => {
        this.spinner.hide(this.spinnerName);
        this.spinnerText = this.defaultSpinnerText;
      });
  }

  /**
   * Update roster data based on user entered roster type rule. 
   * This method is invoked when user clicked Apply button in Configure Roster Code dialog.
   * This function is currently disabled from user interface.
   * @param event CellDoubleClickedEvent
   * @param batchUpdate IEmployeeRosterUpdateByRuleModel
   */
  private updateByRule(event: CellDoubleClickedEvent, batchUpdate: IEmployeeRosterUpdateByRuleModel){
    this.subs.sink = this.rosterService
      .batchUpdateRoster(batchUpdate)
      .subscribe(res => {
        if (res.succeed){
          this.updateEmployeeRosterByValues(event, res.result.employeeRosters[0]);
        }else{
          console.log("Update roster failed " + res.errors.join(" | "));
        }
      });
  }

  /**
   * Update employee roster data.
   * This method is invoked from [[updateByRule]] method which currently disabled.
   * This function is currently disabled.
   * @param rowData 
   * @param employeeRoster 
   */
  private updateEmployeeRosterByValues(event: CellDoubleClickedEvent, employeeRoster: IEmployeeRoster){
    let curEmployeeRoster = this.employeeRosters.find(r => r.employee.id === employeeRoster.employee.id);
    if (curEmployeeRoster){
      for (var day in employeeRoster.values){
        if (employeeRoster.values.hasOwnProperty(day)){
          curEmployeeRoster.values[day] = _.cloneDeep(employeeRoster.values[day]);
        }
      }

      event.node.setData(curEmployeeRoster);
      this.gridApi.redrawRows({rowNodes:[event.node]});
    }
  }

  /**
   * Update roster value based on instruction inside result.
   * @param result 
   */
  private updateEmployeeRoster(result: IEmployeeRosterUpdateModel, chainedAction = null){
    this.spinnerText = "Updating employee's roster data...";
    this.spinner.show(this.spinnerName);

    let from = result.from;
    let to = result.to;
    this.subs.sink = this.rosterService.updateRoster(result)
    .subscribe(res =>{
      if (res.succeed){
        let selectedDays = eachDayOfInterval({start: from, end: to});
        this.updateEmployeeRosterValuesForSelectedDays(result.employeeId, selectedDays, _.cloneDeep(result.rosterValue));
        this.updateSummaryLineData(res.result.rosterSummaryLines);
        if (chainedAction){
          chainedAction();
        }
      }else{
        this.toast.failed("Update roster failed " + res.errors.join(" | "));
      }
    },
    error => {
      this.spinner.hide(this.spinnerName);
      this.spinnerText = this.defaultSpinnerText;
    },
    () =>{
      this.spinner.hide(this.spinnerName);
      this.spinnerText = this.defaultSpinnerText;
    });
  }

  private updateSummaryLineData(summaryLinesDelta: IRosterSummaryLineData[]){
    this.summaryLineData.forEach(sl => {
      summaryLinesDelta.forEach(sld => {
        if (sld.title === sl.title){
          for(var day in sld.data){
            sl.data[day] += sl.data.hasOwnProperty(day) ? sld.data[day] : 0;
          }
        }
      });
    });

    this.pinnedSummaryLineData.forEach(sl => {
      summaryLinesDelta.forEach(sld => {
        if (sld.title === sl.title){
          for(var day in sld.data){
            sl.data[day] += sl.data.hasOwnProperty(day) ? sld.data[day] : 0;
          }
        }
      });
    });

    this.setPinnedBottomRowData(this.pinnedSummaryLineData);
  }

  /**
   * Update roster value and sequence number for selected days. This only update the values loaded on client side. It 
   * will also flash all affected cells using flashCells method from gridApi.
   * @param employeeId selected employee id
   * @param selectedDays selected days to update
   * @param rosterValue new roster value for all selected days for specified employee
   */
  private updateEmployeeRosterValuesForSelectedDays(employeeId: number, selectedDays: Date[], rosterValue: IRosterValue){
    let employeeRoster = this.employeeRosters.find(r => r.employee.id === employeeId);
    if (employeeRoster){
      selectedDays.forEach(day => {
        let dayKey = format(day, "yyyyMMdd");
        employeeRoster.values[dayKey] = _.cloneDeep(rosterValue);
        employeeRoster.values[dayKey].sn = 
          this.util.isDayNightCode(rosterValue.rc.id) ? rosterValue.sn ++ : rosterValue.sn;
      });

      let rowNode = this.getGridRowNode(employeeId);
      rowNode.setData(employeeRoster);

      let columns = [];
      selectedDays.forEach(day => columns.push(`roster.${format(day, "yyyyMMdd")}.rosterCode.code`));
      this.gridApi.flashCells({rowNodes: [rowNode], columns: columns, flashDuration: 1000, fadeDuration: 500});
    }
  }

  public getRosterEmployeeNote(empId: number, colId: string): IRosterEmployeeNote{
    return this.rosterEmployeeNotes[`${empId}-${colId}`];
  }

  private getCellStyle(params){
    var employeeRoster = params.data as IEmployeeRoster;
    let opacity = 0.9;

    if (employeeRoster.isGroupLine){
      return {backgroundColor: "#2f75b5", color: "#ffffff", fontWeight: "bold"};
    }

    // When the cell is summary line
    if (employeeRoster.isSummaryLine){
      return {backgroundColor: "#e2edee", color: "#000000", fontWeight: "bold"};
    }

    let _this = params.context.componentParent;
    let colId = params.colDef.colId;
    // When the cell is Employee Name - DOB
    if (!params.colDef.rosterDay){
      let style = null;
      
      if (_this.isActiveRoster && !employeeRoster.employee.active){
        if ( colId === "employeeName"){
          style = {
            background: "yellow url(./assets/images/roster-note-indicator-red.png) no-repeat right top", 
            opacity: opacity
          };
        }else{
          style = {backgroundColor: "yellow", opacity: opacity};
        }
      }

      let empNote: IRosterEmployeeNote = _this.getRosterEmployeeNote(employeeRoster.employee.id, colId);
      if (empNote && empNote.note && _this.shouldShowRosterEmployeeNoteForm(employeeRoster)){
        style = {
          background: "url(./assets/images/roster-note-indicator-red.png) no-repeat right top", 
          opacity: opacity
        };
      }

      style = style || {opacity: opacity};

      if (employeeRoster.hasOwnProperty("highlights")){
        if (employeeRoster.highlights.hasOwnProperty(colId) && employeeRoster.highlights[colId] === true){
          let highlightStyle = _this.getHighlightStyle();
          style = Object.assign(style, highlightStyle)
        }
      }

      return style;
    }

    let dayStr = format(params.colDef.rosterDay, "yyyyMMdd");    
    let rosterValue = employeeRoster.values[dayStr];

    // When the cell has no roster code assigned
    if (!rosterValue.rc.code){
      if (rosterValue.hl === true){
        var style = _this.getHighlightStyle();
        style.opacity = opacity;
        return style;
      }else{
        return {background: "white", opacity: opacity};
      }
    }

    let rosterColorCodeKey = rosterValue.rc.id + "_" + rosterValue.ts.id;

    // When the roster code has no settings
    if (!_this.rosterCodeSettingsObject.hasOwnProperty(rosterColorCodeKey)){
      return {opacity: opacity};
    }

    let colorObject = _this.rosterCodeSettingsObject[rosterColorCodeKey];
    if (!colorObject){
      return {opacity: opacity};
    }

    colorObject  = _.cloneDeep(colorObject);

    // highlight cell style
    let fontWeight = "normal";
    let fontSize = "0.8rem";
    if (rosterValue.hl === true){
      colorObject.backgroundColor = "#FFFF00";
      colorObject.foregroundColor = "#FF0000";
      fontWeight = "bold";
      fontSize = "1rem";
    }
    
    var rosterCellStyle = {
      background: colorObject.backgroundColor, 
      color: colorObject.foregroundColor,
      fontSize: fontSize,
      fontWeight: fontWeight,
      opacity: opacity
    };

    if (rosterValue.rn && rosterValue.rn.note){
      if (_this.util.isReddish(colorObject.backgroundColor)){
        rosterCellStyle["background"] = 
          `${colorObject.backgroundColor} url(./assets/images/roster-note-indicator-yellow.png) no-repeat right top`;
      }else{
        rosterCellStyle["background"] = 
          `${colorObject.backgroundColor} url(./assets/images/roster-note-indicator-red.png) no-repeat right top`;
      }
    }

    return rosterCellStyle;
  }

  private getHighlightStyle(){
    return {
      backgroundColor: "#FFFF00",
      color: "#FF0000",
      fontSize: "1rem",
      fontWeight: "bold"
    };
  }

  private getDayHeaderClass(params: HeaderClassParams){
    let _this = params.context.componentParent as RosterComponent;
    let dateValue = params.colDef["rosterDay"];

    if (_this.holidayObject.hasOwnProperty(format(dateValue, 'yyyyMMdd'))){
      return "roster-day-holiday";
    }

    if (_this.rosterService.isPayDay(dateValue)){
      return "roster-day-payday";
    }

    return "roster-day";
  }

  private isHoliday(dateValue): boolean{
    let dateStr = format(dateValue, 'yyyyMMdd');
    return (this.holidayObject.hasOwnProperty(dateStr));
  }

  private getRosterValue(params){
    let employeeRoster = params.data as IEmployeeRoster;

    if (employeeRoster.isGroupLine){
      return "";
    }

    let dayStr = format(params.colDef.rosterDay, "yyyyMMdd");
    if (employeeRoster.isSummaryLine){
      let summaryData = params.data as IRosterSummaryLineData
      return summaryData.data[dayStr];
    }

    if (employeeRoster.values[dayStr]){
      return employeeRoster.values[dayStr].sn || employeeRoster.values[dayStr].rc.code;
    }

    return "error";
  }

  private getEmployeeSpan(params){
    let employeeRoster = params.data as IEmployeeRoster;
    return  (employeeRoster.isGroupLine || employeeRoster.isSummaryLine) ? 8 : 1;
  }

  private getDaySpan(params, count){
    let employeeRoster = params.data as IEmployeeRoster;
    return (employeeRoster.isGroupLine) ? count : 1;
  }

  private getRowNo(params){
    console.log(params)
    return params.data.rowNo;
  }

  private getDragText(params){
    let employeeRoster = params.rowNode.data as IEmployeeRoster;
    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      return "";
    }

    return `${employeeRoster.employee?.lastName}, ${employeeRoster.employee?.firstName}`;
  }

  /**
   * Get display name for a roster line. It could be employee's fullname, workgroup name or summary line title
   * This method is called from EmployeeNameRenderer component.
   * @param params 
   * @returns 
   */
  private getDisplayName(params){
    let employeeRoster = params.data as IEmployeeRoster;

    if (employeeRoster.isGroupLine){
      return employeeRoster.employee.workGroup;
    }

    if (employeeRoster.isSummaryLine){
      let summaryData = params.data as IRosterSummaryLineData
      return summaryData.title;
    }

    let _this = params.context.componentParent;
    if (_this.isActiveRoster && !employeeRoster.employee.active){
      return  "";
    }

    return _this.util.getEmployeeName(employeeRoster.employee);
  }

  /**
   * Check if today is the birthday of the employee.
   * This method is called from EmployeeNameRenderer to render a birthday cake before employee's name
   * @param params
   * @returns 
   */
  private isBirthday(params){
    let employeeRoster = params.data as IEmployeeRoster;

    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      return false;
    }

    if (params.context.componentParent.isActiveRoster && !employeeRoster.employee.active){
      return false;
    }

    var month = new Date().getMonth();
    var day = new Date().getDate();
    var dob = new Date(employeeRoster.employee.dateOfBirth);

    return dob.getMonth() === month && dob.getDate() === day;
  }

  private getEmployeePersonalInfo(params){
    let employeeRoster = params.data as IEmployeeRoster;
    let colId = params.colDef.colId;

    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      return "";
    }

    let _this = params.context.componentParent;
    if (_this.isActiveRoster && !employeeRoster.employee.active){
      return "";
    }

    return employeeRoster.employee[colId];
  }

  // this method returns "inactive-employee" for params.value field
  // roster-tooltip component will use this value to provide related content for the tooltip.
  private tooltipForInactiveEmployeeValueGetter(params){
    let employeeRoster = params.data as IEmployeeRoster;

    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine ){
      return "";
    }

    let _this = params.context.componentParent;
    let colId = params.colDef.colId;
    let empId = employeeRoster.employee.id;

    
    let note = _this.getRosterEmployeeNote(empId, colId);
    if (note && note.note && _this.shouldShowRosterEmployeeNoteForm(employeeRoster)){
      return "employee-note";
    }else{
      if (_this.isActiveRoster && !employeeRoster.employee.active){
        return "inactive-employee";
      }
    }

    return "";
  }

  private tooltipValueGetter(params){
    if (params.location === "header"){
      return params.colDef.rosterDay;
    }

    if (params.location === "cell"){
      var day = format(params.colDef.rosterDay, "yyyyMMdd");
      var rosterNote = params.data.values ? params.data.values[day].rn : null;
      if (rosterNote && rosterNote.note){
        return "roster-note";
      }
    }
    
    return "";
  }

  private getDOB(params){
    let employeeRoster = params.data as IEmployeeRoster;

    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      return "";
    }

    let _this = params.context.componentParent;
    if (_this.isActiveRoster && !employeeRoster.employee.active){
      return "";
    }

    return _this.util.getDateString(employeeRoster.employee.dateOfBirth);
  }  

  private getAccessId(params){
    let employeeRoster = params.data as IEmployeeRoster;

    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      return "";
    }

    let _this = params.context.componentParent;
    if (_this.isActiveRoster && !employeeRoster.employee.active){
      return "";
    }

    return employeeRoster.employee.accessId;
  }  

  private draggableRow(params){
    let employeeRoster = params.data as IEmployeeRoster;
    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      return false;
    }

    return true;
  }

  private showActionComponent(empRoster: IEmployeeRoster):boolean{
    if (!this.canModifyRoster()){
      return false;
    }

    if (empRoster.isGroupLine || empRoster.isSummaryLine){
      return false;
    }

    return this.isActiveRoster && !empRoster.employee.active;
  }

  private getActionComponentTooltip(empRoster: IEmployeeRoster):string{
    return "Remove line from roster";
  }

  private getActionComponentIcon(empRoster: IEmployeeRoster):string{
    return "fas fa-times"
  }

  private updateEmployeeSortNo(whenDone = null){
    if (!this.canModifyEmployeeRosterSortNo()){
      if (whenDone){
        whenDone();
      }

      return;
    }

    let grpsToReOrder = {};
    
    this.employeeRosters.forEach((er, index)=>{
      if (!er.isGroupLine && !er.isSummaryLine){
        if (index !== this.employeeOrders[er.employee.id] && !grpsToReOrder.hasOwnProperty(er.employee.workGroupId)){
          grpsToReOrder[er.employee.workGroupId] = 1;
        }
      }
    });

    let index = 0;
    let employeesToUpdate: IEmployeeSortNoUpdateModel[] = [];
    this.employeeRosters.forEach((er)=>{
      index = (er.isGroupLine && grpsToReOrder.hasOwnProperty(er.employee.workGroupId)) ? 0 : index + 1;

      if (!er.isGroupLine && 
        !er.isSummaryLine && 
        grpsToReOrder.hasOwnProperty(er.employee.workGroupId)){
        employeesToUpdate.push({employeeId: er.employee.id, rosterSortNo: index });
      }
    });

    if (employeesToUpdate.length > 0){
      this.subs.sink = this._empService.updateEmployeeRosterSortNo(employeesToUpdate)
        .subscribe(
          res => {},
          err => {},
          () => {
            if (whenDone){
              whenDone();
            }
        });
    }else{
      if (whenDone){
        whenDone();
      }
    }
  }

  private canColumnBeHighlighted(colId: string): boolean{
    return colId === "employeeName" || 
      colId === "phone" || 
      colId === "email" || 
      colId === "dateOfBirth" ||
      colId === "company" ||
      colId === "position" ||
      colId === "supervisor" ||
      colId === "workarea" ||
      colId === "accessId";
  }

  /**
   * Load data required for ag-grid:
   * 1. System defined date range. (Defined in [[PpsUtilityService]])
   * 2. Set initial date range for the roster: one month data from today
   * 3. Load entities: roster codes, roster code settings, holidays
   */
  private initialize(){
    let dateRanges = this.util.getDateRanges();
    this.dateRanges = Object.keys(dateRanges).map(key => { return {id: key, title: dateRanges[key].title}; });

    // By default, display one month roster data from today
    let startDate = this.util.getDateOnly(new Date());
    this.setDateRange(startDate, addMonths(startDate, 1));
    this.loadEntitiesAndRosterData();
  }

  /**
   * Set date range of the roster module
   * @param from beginning date
   * @param to endding date
   */
  private setDateRange(from:Date, to:Date){
    this.startDate = from;
    this.endDate = to;
  }

  /**
   * Load roster data, highlight cell with the following entities: 
   * roster codes, roster code settings, holidays, entities required for edit employee form:
   * supervisors, work groups, work areas, positions, roster types
   * Roster code settings are converted to a dictionary with key set to {rosterCodeId_ticketStatusId}, value set to 
   * {foregroundColor, backgroundColor}.
   */
  private loadEntitiesAndRosterData(){
    this.spinner.show(this.spinnerName);
    this.employeeRosters = [];
    this.summaryLineData = [];
    this.pinnedSummaryLineData = [];
    var shouldLoadEntitiesForEditEmployee = this.canModifyEmployee();
    this.subs.sink = forkJoin([
      this.rosterService.getRostersWithEntities(
        this.rosterProject.id,
        this.startDate, 
        this.endDate, 
        true, // load with entities
        shouldLoadEntitiesForEditEmployee,
        this.isActiveRoster ? true : null, 
        this.isActiveRoster ? null : false),
    ]).subscribe(([res]) => {
      if (res.succeed){
        this.rosterCodes = res.result.rosterCodes;

        res.result.rosterCodeSettings.forEach( rcs => {
          let travelStatus = rcs.ticketStatus || {id: 0, description: ""};
          this.rosterCodeSettingsObject[rcs.rosterCode.id + "_" + travelStatus.id] = {
            foregroundColor: rcs.foregroundColor, backgroundColor: rcs.backgroundColor
          }
        });

        res.result.holidays.forEach(hd => {
          this.holidayObject[format(hd.day, "yyyyMMdd")] = hd.holidayName;
        });

        this.employeeUpdateEntities = {
          supervisors: res.result.supervisors,
          workGroups: res.result.workGroups,
          positions: res.result.positions,
          rosterTypes: res.result.rosterTypes,
          workAreas: res.result.workAreas,
          employers: res.result.employers
        };
        
        res.result.rosterEmployeeNotes.forEach(n=>{
          this.rosterEmployeeNotes[`${n.employeeId}-${n.columnName}`] = n;
        });
        
        this.setupGridData(res.result.rosterData, res.result.employeeHighlights);

      }else{
        this.toast.failed(`Failed to load roster data: ${res.errors.join(";")}`);
        return;
      }
      
      
    },
    error => {
      this.toast.failed(`Failed to load roster data: ${error.statusText}. Please try again later.`);
      this.spinner.hide(this.spinnerName)
    },
    () => this.spinner.hide(this.spinnerName));
  }

  /**
   * Reload roster data via [[_loadRosterDataHelper]] method. 
   * If the user can change roster employee display order, this method will make sure
   * all updated employees display order will be saved before the roster data are reloaded.
   */
  private _loadRosterData(){
    if (this.canModifyEmployeeRosterSortNo()){
      this.updateEmployeeSortNo(() => this._loadRosterDataHelper());
    }else{
      this._loadRosterDataHelper();
    }
  }

  /**
   * Load roster data based on current start date and end date. This methods will load roster data
   * and highlighted columns for roster employees. The highlighted columns will be merged to highlight
   * property defined in IEmployeeRoster.
   */
  private _loadRosterDataHelper(){
    this.spinner.show(this.spinnerName);
    this.spinnerText = "Loading roster data ...";
    this.employeeRosters = [];
    this.summaryLineData = [];
    this.pinnedSummaryLineData = [];

    this.subs.sink = forkJoin([
      this.rosterService.getRostersWithEntities(
        this.rosterProject.id,
        this.startDate, 
        this.endDate, 
        false,
        false,
        this.isActiveRoster ? true : null, 
        this.isActiveRoster ? null : false),
    ]).subscribe(
      ([res]) => {
        res.result.rosterEmployeeNotes.forEach(n=>{
          this.rosterEmployeeNotes[`${n.employeeId}-${n.columnName}`] = n;
        });
        this.setupGridData(res.result.rosterData, res.result.employeeHighlights);
      },
      error => {
        this.toast.failed(`Failed to load roster data: ${error.statusText}. Please try again later.`);
        this.spinner.hide(this.spinnerName);
      },
      () => this.spinner.hide(this.spinnerName)
    );
  }

  /**
   * Setup roster grid data
   * @param rosterData IRosterResult
   * @param empHighlights IRosterEmployeeHighlight[]
   */
  private setupGridData(rosterData: IRosterResult, empHighlights: IRosterEmployeeHighlight[]){
    this.employeeRosters = rosterData.employeeRosters;
    this.employeeRosters.forEach((e, index) => this.employeeRosters[index].rowNo = index + 1);
  
    // transform employee highlight array to dictionary
    let employeeHighlights: IEmployeeHighlight = {};
    empHighlights.forEach(e => {
      if (!employeeHighlights.hasOwnProperty(e.employeeId)){
        employeeHighlights[e.employeeId] = {};
      }

      employeeHighlights[e.employeeId][e.columnName] = e.active;
    });

    this.employeeRosters.forEach((er, index)=>{
      // store the original employee's display order index
      if (!er.isGroupLine && !er.isSummaryLine){
        this.employeeOrders[er.employee.id] = index;
      }

      // merge roster employee highlight information to
      if (employeeHighlights.hasOwnProperty(er.employee.id)){
        er.highlights = employeeHighlights[er.employee.id];
      }
    });

    this.setDateRange(this.startDate, this.endDate);
    this.getColumnDefs();

    if (this.isActiveRoster){
      this.summaryLineData =  rosterData.rosterSummaryLines;
      
      if (this.storedSummaryLines.length === 0){
        this.pinnedSummaryLineData = _.cloneDeep(this.summaryLineData);
      }

      this.summaryLineData.forEach(sl => {
        if (this.storedSummaryLines.includes(sl.title)){
          this.pinnedSummaryLineData.push(_.cloneDeep(sl));
        }
      });
    }else{
      this.pinnedSummaryLineData = [];
    }
  }

  /**
   * Get ag-grid column definitions. 
   */
  private getColumnDefs(){
    this.gridHeaders = [
      {
        headerName: environment.clientName,
        colId: "employee",
        suppressMovable: true,
        cellStyle: "{'textAlign': 'left'}",
        headerClass: "roster-title",
        children:[
          {
            headerName: `${this.rosterProject.title} Roster`,
            colId: "employee",
            suppressMovable: true,
            cellStyle: "{'textAlign': 'left'}",
            headerClass: "roster-title-2",
            children:[
              {
                headerName: 'Employee Name',
                colId: "employeeName",
                field:'displayName',
                rowDrag: this.draggableRow,
                rowDragText: this.getDragText,
                width: 200,
                filter: EmployeeFilterComponent,
                filterParams: {
                  employees: this.getEmployees()
                },
                floatingFilter: true,
                floatingFilterComponent: EmployeeFloatingFilterComponent,
                cellRenderer: EmployeeNameRenderer,
                cellStyle: this.getCellStyle,
                colSpan: this.getEmployeeSpan,
                tooltipComponent: 'customTooltip',
                tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
              }
            ]
          }
        ]
      }
    ];

    if (this.isActiveRoster && this.canModifyRoster()){
      
      this.gridHeaders[0].children[0].children.splice(0, 0, {
        headerName: "",
        field: "action",
        cellRenderer: DeleteActionComponent,
        width: 24,
        resizable: false,
        cellStyle: {textAlign:"center"}            
      });
    }

    if (!this.isActiveRoster && this.canModifyRoster()){
      
      this.gridHeaders[0].children[0].children.splice(0, 0, {
        headerName: "",
        field: "action",
        cellRenderer: GridActionComponent,
        width: 24,
        resizable: false,
        cellStyle: {textAlign:"center"}            
      });
    }

    if (!this.smallScreen){
      this.gridHeaders[0].children[0].children = this.gridHeaders[0].children[0].children.concat([
        {
          headerName: 'Company',
          colId: "company",
          field:'employee.employer',
          width: 100,
          filter: true,
          floatingFilter: true,
          cellStyle: this.getCellStyle,
          tooltipComponent: 'customTooltip',
          tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
        },
        {
          headerName: 'Position',
          colId: "position",
          field:'employee.position',
          width: 120,
          filter: true,
          floatingFilter: true,
          //valueGetter: this.getEmployeePosition,
          cellStyle: this.getCellStyle,
          tooltipComponent: 'customTooltip',
          tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
        },
        {
          headerName: 'Supervisor',
          colId: "supervisor",
          field:'employee.supervisor',
          width: 120,
          filter: true,
          floatingFilter: true,
          //valueGetter: this.getEmployeeSupervisor,
          cellStyle: this.getCellStyle,
          tooltipComponent: 'customTooltip',
          tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
        },
        {
          headerName: 'Work Area',
          colId: "workarea",
          field:'employee.workArea',
          width: 120,
          filter: true,
          floatingFilter: true,
          cellStyle: this.getCellStyle,
          tooltipComponent: 'customTooltip',
          tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
        },
        {
          headerName: 'Contact',
          colId: "phone",
          field:'employee.phone',
          width: 90,
          cellStyle: this.getCellStyle,
          valueGetter: this.getEmployeePersonalInfo,
          tooltipComponent: 'customTooltip',
          tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
        },
        {
          headerName: 'Email',
          colId: "email",
          field:'employee.email',
          width: 80,
          cellStyle: this.getCellStyle,
          valueGetter: this.getEmployeePersonalInfo,
          tooltipComponent: 'customTooltip',
          tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
        },
        {
          headerName: 'DOB',
          colId: "dateOfBirth",
          field:'employee.dateOfBirth',
          width: 80,
          cellStyle: this.getCellStyle,
          comparator: (date1, date2) => this.util.dateComparator(date1, date2),
          valueGetter: this.getDOB,
          tooltipComponent: 'customTooltip',
          tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
        },
        {
          headerName: 'Access Id',
          colId: "accessId",
          field:'employee.accessId',
          width: 80,
          cellStyle: this.getCellStyle,
          valueGetter: this.getAccessId,
          tooltipComponent: 'customTooltip',
          tooltipValueGetter: this.tooltipForInactiveEmployeeValueGetter
        }
      ]);
    }

    let years = eachYearOfInterval({start: this.startDate, end: this.endDate});
    let months = eachMonthOfInterval({start: this.startDate, end: this.endDate});

    // // // years.map(d => d.getFullYear()).forEach( year =>{
    // // //   let yearHeader = {
    // // //     headerName: year + "",
    // // //     headerClass: "roster-year",
    // // //     pinned: false,
    // // //     resizable: false,
    // // //     suppressMovable: true,
    // // //     children:[]
    // // //   }

    // // //   this.gridHeaders.push(yearHeader);

    months.forEach( mon => {
      // // // if (mon.getFullYear() != year){
      // // //   return;
      // // // }

      let monthHeader = {
        headerName: format(mon, 'MMMM/yyyy'),
        headerClass: "roster-month",
        pinned: false,
        resizable: false,
        suppressMovable: true,
        children: []
      };

      /////////////////yearHeader.children.push(monthHeader);
      
      this.gridHeaders.push(monthHeader);

      let start = max([mon, this.startDate]);
      let end = min([lastDayOfMonth(mon), this.endDate]);
      let days = eachDayOfInterval({start: start, end:end});

      days.forEach(day => {
        let payDay = this.rosterService.isPayDay(day);
        let holiday = this.isHoliday(day);

        let dayHeader = {
          headerName: format(day, "EEE"),
          headerClass: this.getDayHeaderClass,
          headerTooltip: format(day, 'dd/MM/yyyy'),
          pinned: false,
          resizable: false,
          suppressMovable: true,
          rosterDay: day,
          children:[{
            headerName: format(day, "dd"),
            headerComponentParams: {
              template:`
                <div class="ag-cell-label-container" role="presentation">
                <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
                <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                    <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                    ${payDay ? '<i class="ml-1 fas fa-dollar-sign"></i>' : ''}
                    ${holiday ? '<i class="ml-1 fas fa-umbrella-beach"></i>' : ''}
                    <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                    <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                    <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                    <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                    <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                  </div>
                </div>
              `
            },
            headerClass: this.getDayHeaderClass,
            headerTooltip: holiday ? this.holidayObject[format(day, "yyyyMMdd")] : null,
            rosterDay: day,
            colId: format(day, "yyyyMMdd"),
            field: "roster." + format(day, "yyyyMMdd") + ".rosterCode.code",
            valueGetter: this.getRosterValue,
            cellClass: "roster-day-value",
            cellStyle: this.getCellStyle,
            tooltipComponent: 'customTooltip',
            tooltipValueGetter: this.tooltipValueGetter,
            pinned: false,
            resizable: false,
            suppressMovable: true,
            colSpan: null,
            width: 35
          }]
        };
        dayHeader.children[0].colSpan = (params) => this.getDaySpan(params, days.length);
        monthHeader.children.push(dayHeader);
      });
    });
  // // // })
  }

  /**
   * Highlight the selected column (colId) for given employee (empId)
   * @param rowNode The RowNode object defined in ag-grid
   * @param empId The employee Id
   * @param colId The colId used in grid column definition. See method [[getColumnDefs]]
   */
  private highlightEmployee(rowNode: RowNode, empRoster: IEmployeeRoster, update: IEmployeeHighlightUpdate){
    if (this.canModifyRoster()){
      this.subs.sink = this.rosterService.highlightEmployeeSection(update)
        .subscribe(res =>{
          if (res.succeed){
            res.result.forEach(eh => {
              empRoster.highlights = empRoster.highlights || {};
              empRoster.highlights[eh.columnName] = eh.active;
            });
            
            rowNode.setData(empRoster);
            this.gridApi.redrawRows({rowNodes:[rowNode]});      
          }else{
            this.toast.failed(`Failed to highlight ${update.colId}`);
          }
        });
    }
  }


  /**
   * Paste roster value stored in clipboard to target cell via [[updateEmployeeRoster]] method. 
   * Roster note will NOT be pasted over. 
   * This method is called when ctrl+v clicked event is detected
   * @param event CellKeyDownEvent defined in ag-grid
   */
   private pasteRosterData(event: CellKeyDownEvent){
    if (this.clipboardData){
      let result = {
        employeeId: event.data.employee.id,
        section: 2,
        oldRosterValue: this.getRosterValueFromSelectedCell(event),
        rosterValue: _.cloneDeep(this.clipboardData),
        from: event.colDef["rosterDay"],
        to: event.colDef["rosterDay"]
      };

      // do not copy over the note but keep the old roster note if any
      result.rosterValue.rn = result.oldRosterValue.rn; 
      
      this.checkAndUpdatePayrollRosterCode(result.employeeId, result.to, result.rosterValue, result);
    }
  }

  private checkAndUpdatePayrollRosterCode(employeeId: number, day: Date, currentPayrollRosterValue: IRosterValue, result: any){
    let prevDateStr = format(subDays(day, 1), "yyyyMMdd");
    let nextDateStr = format(addDays(day, 1), "yyyyMMdd");

    let employeeRoster = this.employeeRosters.find(ep => ep.employee.id === employeeId);
    if (employeeRoster){
      let previousPayrollRosterValue = employeeRoster.values[prevDateStr];
      let nextRosterValue = employeeRoster.values[nextDateStr];

      if (!nextRosterValue || !previousPayrollRosterValue){
        this._confirm.confirm({
          header: "Load more roster data",
          message: `Please load more roster data to continue. The system needs to know previous day and next day's roster data for validation`,
          icon: "pi pi-ban text-danger",
          key: "invalidPayrollRosterCodePromptDialog1",
          acceptVisible: true,
          rejectVisible: false,
          acceptLabel: "OK"
        });

        return;
      }

      let recommentedPayrollRosterCode = 
      this._rosterCodeService.getRosterCode(
        this._payrollRosterCodeRecommendor.recommend(currentPayrollRosterValue, previousPayrollRosterValue, nextRosterValue)
      );
  
      if (recommentedPayrollRosterCode?.id !== currentPayrollRosterValue.prc?.id){
        this._confirm.confirm({
          header: "Incorrect Payroll Roster Code",
          message: `The source payroll roster code is ${currentPayrollRosterValue.prc?.code || "not set"}. We recommend changing to ${recommentedPayrollRosterCode?.code}`,
          icon: "pi pi-ban text-danger",
          key: "invalidPayrollRosterCodePromptDialog1",
          acceptVisible: true,
          acceptLabel: "Accept Change",
          rejectLabel: "Ignore Change",
          accept: () => {
            result.rosterValue.prc = recommentedPayrollRosterCode;
            this.updateEmployeeRoster(result);
          },
          reject: () => {
            this.updateEmployeeRoster(result);
          }
        });      
      }else{
        this.updateEmployeeRoster(result);
      }
    }
  }

  /**
   * Delete roster value from selected cell. 
   * This method is called when Delete key is pressed
   * @param event CellKeyDownEvent defined in ag-grid
   */
  private deleteRosterData(event: CellKeyDownEvent){
    let rosterValue = this.getRosterValueFromSelectedCell(event);
    let employeeRoster = event.data as IEmployeeRoster;

    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      return;
    }
      
    if (!rosterValue.rc?.id){
      return;
    }

    this._confirm.confirm({
      header: `Delete Roster Value`,
      message: `Please confirm to delete the roster value for "${this.util.getEmployeeName(employeeRoster.employee)}".`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => {
        let result: any = {
          employeeId: employeeRoster.employee.id,
          section: 2,
    
          oldRosterValue: rosterValue,
          rosterValue: {
            rc: {id: 0, code: null},
            prc: {id: 0, code: null},
            sn: null,
            ts: {id: 0, description: null}
          },
          from: event.colDef["rosterDay"],
          to: event.colDef["rosterDay"]
        };
    
        this.updateEmployeeRoster(result);
      }
    });
  }

  /**
   * Get the clone of roster value from selected cell. If selected cell is not a roster code cell, it will return null.
   * @param event CellKeyDownEvent defined in ag-grid
   * @returns null or the clone of roster value from selected cell.
   */
  private getRosterValueFromSelectedCell(event: CellKeyDownEvent){
    let rosterValue: IRosterValue = null;
    let employeeRoster = event.data as IEmployeeRoster;

    if (employeeRoster.isGroupLine || employeeRoster.isSummaryLine){
      rosterValue = null;
    }else{
      let dayStr = format(event.colDef["rosterDay"], "yyyyMMdd");
      rosterValue = employeeRoster.values[dayStr];
    }

    return _.cloneDeep(rosterValue);
  }

  /**
   * Generate roster data based on selected target date and roster type configured for employees. 
   */
  private generateRosterDataHelper(){
    this.spinnerText = "Generating employee's roster data...";
    this.spinner.show(this.spinnerName);
    this.subs.sink = this.rosterService.generateRosterData(this.rosterProject.id, {
        endDate: this.generateRosterDataEndDate
      }).subscribe(
        (res) => {
          this.spinner.hide(this.spinnerName);
          this.spinnerText = this.defaultSpinnerText;

          if (res.succeed){
            window.setTimeout(() => {this.refresh()},0);
          }else{
            this.toast.failed("Generate roster data failed " + res.errors.join(";"));
          }
        },
        error => {
          this.spinner.hide(this.spinnerName);
          this.spinnerText = this.defaultSpinnerText;
        }
    );
  }

  /**
   * Delete an employee line from roster. 
   * @param empRoster IEmployeeRoster to be deleted
   */
   private deleteHelper(empRoster: IEmployeeRoster){
    this.spinnerText = "Deleting employee's roster data...";
    this.spinner.show(this.spinnerName);
    this.subs.sink = this._empService.removeEmployeeFromRoster(empRoster.employee)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.searchEmployeeRoster(empRoster.employee?.id, (foundIndex)=>{
            this.employeeRosters.splice(foundIndex, 1);
            this.setGridRowData(this.employeeRosters);
          });
        }
      },
      error => {
        this.spinner.hide(this.spinnerName);
        this.spinnerText = this.defaultSpinnerText;
      },
      () => {
        this.spinner.hide(this.spinnerName);
        this.spinnerText = this.defaultSpinnerText;
      });
  }

  /**
   * Lookup employee based on employee id and execute the callback method whenFound once it is found.
   * @param empoyeeId The employee to lookup
   * @param whenFound Callback function once a employee has been found. The callback function takes the index of the 
   * employee as parameter
   */
   private searchEmployeeRoster(empoyeeId: number, whenFound){
    var foundIndex = this.employeeRosters.findIndex(er => er.employee?.id === empoyeeId);
    if (foundIndex !== -1 && whenFound){
      whenFound(foundIndex);
    }
  }

  /**
   * Get employee list for employee filter. Sort the employee list by their full name
   * @returns ISelectOption[]
   */
  private getEmployees(): ISelectOption[]{
    var employees: ISelectOption[] = [];

    this.employeeRosters.forEach(e => {
      if (e.employee.id > 0){
        let fullname = this.util.getEmployeeName(e.employee);
        employees.push({
          value: fullname.toLowerCase(),
          text: fullname
        })
      }
    })

    employees = employees.sort((a, b) => {
      if ( a.value < b.value){
        return -1;
      }
      if ( a.value > b.value){
        return 1;
      }
      return 0;      
    });

    return employees;
  }

  /**
   * Show employee form from employee module. Allow edit employee properties from roster module.
   * To edit employee, user 1) must have Modify Employee permission and 2) employee update entities must have been
   * loaded successfully.
   * @param employee IRosterEmployeeViewModel
   * @returns 
   */
   public editEmployee(employee: IRosterEmployeeViewModel){
    if (this.canModifyEmployee()){
      if (this.employeeUpdateEntities === null){
        return;
      }

      this.spinnerText = `Loading ${this.util.getEmployeeName(employee)}'s employee data...`;
      this.spinner.show(this.spinnerName);
      this.subs.sink = this._empService.getEmployeeById(employee.id)
        .subscribe(
          empRes => {
            if (empRes.succeed){
              this.openEmployeeForm(empRes.result);
            }
          },
          error=>{
            this.spinner.hide(this.spinnerName); 
            this.spinnerText = this.defaultSpinnerText;
          },
          () => {
            this.spinner.hide(this.spinnerName);
            this.spinnerText = this.defaultSpinnerText;
          }
        );
    }
  }

  /**
   * Show employee edit form
   * @param employee Employee
   * @returns 
   */
  private openEmployeeForm(employee: Employee){
    if (employee === null){
      return;
    }

    let dialog = this._dialog.open(EmployeeFormComponent, {
      header: "Edit Employee", 
      styleClass: "size-md",
      data:{
        employee: employee,
        employees: this.employeeUpdateEntities.supervisors,
        workGroups: this.employeeUpdateEntities.workGroups,
        positions: this.employeeUpdateEntities.positions,
        rosterTypes: this.employeeUpdateEntities.rosterTypes,
        workAreas: this.employeeUpdateEntities.workAreas,
        employers: this.employeeUpdateEntities.employers
      },
      draggable: true
    });


    this.subs.sink = dialog.onClose.subscribe(employee => {
      if (employee){ 
        // clicked Save button
        this.saveEmployee(employee);
      }
    });
  }

  /**
   * Save employee to database and flash updated cells after the data successfully saved.
   * @param employee Employee
   */
  private saveEmployee(employee: Employee){
    this.subs.sink = this._empService
    .updateEmployee(employee)
    .subscribe(response => {
      if (response.succeed){
        this.searchEmployeeRoster(employee.id, (index)=>{
          let columns = [];
          var oldEmployee = this.employeeRosters[index].employee;

          if (oldEmployee.employer !== employee.employer?.employerName){
            oldEmployee.employer = employee.employer?.employerName;
            columns.push("company");
          }

          if (oldEmployee.position !== employee.position.title){
            oldEmployee.position = employee.position.title;
            columns.push("position");
          }

          if (oldEmployee.supervisor !== this.util.getEmployeeName(employee.supervisor, true)){
            oldEmployee.supervisor = this.util.getEmployeeName(employee.supervisor, true);
            columns.push("supervisor");
          }

          if (oldEmployee?.workArea !== employee.workArea?.areaName){
            oldEmployee.workArea = employee.workArea?.areaName;
            columns.push("workarea");
          }

          if (oldEmployee.phone !== employee.phone){
            oldEmployee.phone = employee.phone;
            columns.push("phone");
          }

          if (oldEmployee.email !== employee.email){
            oldEmployee.email = employee.email;
            columns.push("email");
          }

          let newDate = new Date(employee.dateOfBirth);
          let oldDateStr = format(oldEmployee.dateOfBirth, "MM/dd/yyyy");
          let newDateStr = format(newDate, "MM/dd/yyyy");

          if (oldDateStr !== newDateStr){
            oldEmployee.dateOfBirth = newDate;
            columns.push("dateOfBirth");
          }          

          let rowNode = this.getGridRowNode(employee.id);
          if (rowNode){
            rowNode.setData(this.employeeRosters[index]);
            this.gridApi.flashCells({rowNodes: [rowNode], columns: columns, flashDuration: 1000, fadeDuration: 500});
          }
        });
      }else{
        this.toast.failed("Employee update failed: " + response.errors.join(";"));
      }
    });
  }

  // -------------------------------------------------------------------------------------------------------------------
  // The following methods are provided for GridActionComponent
  // -------------------------------------------------------------------------------------------------------------------
  private getGridActionIcon(){
    return "fas fa-plus";
  }

  private getGridActionClass(){
    return "text-success";
  }

  private getGridActionTooltip(){
    //return "Restore a replacement blank line to active roster.";
    return "Click to add this employee to active roster.";
  }

  private showGridActionComponent(empRoster: IEmployeeRoster):boolean{
    if (!this.canModifyRoster()){
      return false;
    }

    if (empRoster.isGroupLine || empRoster.isSummaryLine){
      return false;
    }

    return !this.isActiveRoster && !empRoster.employee.showInRoster;
  }

  /**
   * Callback function to add an employee line to active roster. 
   * This method is invoked from GridActionComponent. 
   * @param empRoster IEmployeeRoster of the selected row
   * @returns 
   */
   execGridAction(empRoster: IEmployeeRoster){
    if (empRoster === null){
      return;
    }

    this._confirm.confirm({
      header: `Add employee to active roster`,
      message: `${this.util.getEmployeeName(empRoster.employee)} will be added to active roster. Please confirm.`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.addEmplpoyeeToActiveRoster(empRoster)
    });
  }  

  /**
   * Add an employee line to active roster. 
   * @param empRoster IEmployeeRoster to be deleted
   */
   private addEmplpoyeeToActiveRoster(empRoster: IEmployeeRoster){
    this.subs.sink = this._empService.addEmployeeToActiveRoster(empRoster.employee)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.searchEmployeeRoster(empRoster.employee?.id, (foundIndex)=>{
            //this.employeeRosters.splice(foundIndex, 1);
            empRoster.employee.showInRoster = true;
            this.setGridRowData(this.employeeRosters);
          });
        }
      });
  }

  private getSummaryLineDisplaySettingsKey(): string{
    return `${this.summaryLineDisplaySettingsKey}_${this.rosterProject.id}`;
  }

  private getRosterEmployees(): IEmployeeSimpleModel[]{
    let emps: IEmployeeSimpleModel[] = [];
    this.employeeRosters.forEach(er => {
      if (er.employee.id !== 0){
        emps.push({
          id: er.employee.id, 
          firstName: er.employee.firstName, 
          lastName: er.employee.lastName
        });
      }
    });

    //emps.sort((a, b)=>a.lastName < b.lastName ? -1 : a.lastName > b.lastName ? 1 : a.firstName < b.firstName ? -1 : a.firstName > b.firstName ? 1 : 0);

    return emps;
  }
}
